import React, { useEffect, useState } from "react";
import { useFieldArray, useForm, useFormContext } from "react-hook-form";
import { Button } from "../ui/button";
import {
  DEFAULT_OPENING_HOURS,
  PickUpLocationFormData,
  pickUpLocationSchema,
  RegisterFormData,
} from "../../types/forms/auth/registerFormData.form";
import { zodResolver } from "@hookform/resolvers/zod";
import { PickUpLocationForm } from "../pickUpLocation/PickUpLocationForm";
import { EntityCard } from "./EntityCard";

interface RegisterStepPickUpLocationProps {
  onSubmit: () => void;
  onBack: () => void;
}

const DEFAULT_PICK_UP_LOCATION: PickUpLocationFormData = {
  name: "",
  street: "",
  postalCode: "",
  city: "",
  contactNumber: "",
  contactNumber2: "",
  openingHours: DEFAULT_OPENING_HOURS,
  isDefault: false,
};

const RegisterPickUpLocation: React.FC<RegisterStepPickUpLocationProps> = ({
  onSubmit,
  onBack,
}) => {
  const [statePickUpLocationForm, setStatePickUpLocationForm] = useState<{
    editIndex: number | undefined;
    show: boolean;
  }>({ editIndex: undefined, show: false });

  const { control } = useFormContext<RegisterFormData>();
  const { fields, remove, update, append } = useFieldArray({
    control,
    name: "pickUpLocations" as const,
  });

  const pickUpLocationForm = useForm<PickUpLocationFormData>({
    resolver: zodResolver(pickUpLocationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: DEFAULT_PICK_UP_LOCATION,
  });

  useEffect(() => {
    if (fields.length === 0) {
      setStatePickUpLocationForm({ editIndex: undefined, show: true });
    }
  }, [fields.length]);

  const handleSavePickUpLocation = async (data: PickUpLocationFormData) => {
    if (statePickUpLocationForm.editIndex !== undefined) {
      update(statePickUpLocationForm.editIndex, data);
    } else {
      append(data);
    }
    pickUpLocationForm.reset(DEFAULT_PICK_UP_LOCATION);
    setStatePickUpLocationForm({ editIndex: undefined, show: false });
    window.scrollTo(0, 0);
  };

  const handleCancelPickUpLocation = () => {
    setStatePickUpLocationForm({ editIndex: undefined, show: false });
    pickUpLocationForm.reset(DEFAULT_PICK_UP_LOCATION);
  };

  const pickUpLocationFormContent = (
    <PickUpLocationForm
      control={pickUpLocationForm.control}
      watch={pickUpLocationForm.watch}
      setValue={pickUpLocationForm.setValue}
      errors={pickUpLocationForm.formState.errors}
      showIsDefault={false}
      register={pickUpLocationForm.register}
    />
  );

  return (
    <div className="flex flex-col gap-8 py-28 md:px-20 lg:px-8">
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="font-jakarta-sans text-3xl font-semibold text-black">Créez votre compte</h1>
        <p className="text-base text-gray-600">Entrez vos informations pour créer votre espace.</p>
      </div>

      <div className="flex flex-col gap-4">
        <h2 className="text-base font-semibold text-blue-600">Lieu d'enlèvement par défaut</h2>

        {fields.map((_item, index) => {
          const isEditing =
            statePickUpLocationForm.show && statePickUpLocationForm.editIndex === index;
          return (
            <EntityCard
              key={index}
              isEditing={isEditing}
              onEdit={() => {
                setStatePickUpLocationForm({ editIndex: index, show: true });
                pickUpLocationForm.reset(fields[index]);
              }}
              onSave={pickUpLocationForm.handleSubmit(handleSavePickUpLocation)}
              onCancel={handleCancelPickUpLocation}
              onRemove={() => remove(index)}
              summary={
                <>
                  <p>Lieu d'enlèvement</p>
                  <p>{fields[index]?.name}</p>
                </>
              }
              form={pickUpLocationFormContent}
            />
          );
        })}

        {statePickUpLocationForm.show && statePickUpLocationForm.editIndex === undefined && (
          <EntityCard
            isEditing
            summary={null}
            form={pickUpLocationFormContent}
            onSave={pickUpLocationForm.handleSubmit(handleSavePickUpLocation)}
            onCancel={fields.length > 0 ? handleCancelPickUpLocation : undefined}
          />
        )}

        {!statePickUpLocationForm.show && (
          <Button
            onClick={() => {
              setStatePickUpLocationForm({ editIndex: undefined, show: true });
              pickUpLocationForm.reset(DEFAULT_PICK_UP_LOCATION);
            }}
            variant="tertiary"
          >
            Ajouter un lieu supplémentaire
          </Button>
        )}
      </div>

      <div className="flex flex-row justify-end gap-2">
        <Button variant="secondary" onClick={onBack}>
          Retour
        </Button>
        {fields.length > 0 && !statePickUpLocationForm.show && (
          <Button onClick={onSubmit}>Suivant</Button>
        )}
      </div>
    </div>
  );
};

export default RegisterPickUpLocation;
